import dayjs from 'dayjs';
import {
  pick,
  getFullName,
  diffArrays,
  mapObject,
  filterObject,
  isEmpty,
  omit,
} from '@lib/help-fns';
import { diff } from 'deep-object-diff';
import { DOCUMENTS, EMPLOYEE_LIST_ENTITIES } from '../../model/static';

/**
 * @typedef {import('../../model/profile').Employee} Employee
 */

/**
 * Returns employee's basic information difference
 * @param {Employee} prevValues
 * @param {Employee} values
 * @returns {Object}
 */
export function getEmployeeBasicDiff(prevValues, values) {
  const prevBasic = omit(EMPLOYEE_LIST_ENTITIES, prevValues);
  const nextBasic = omit(EMPLOYEE_LIST_ENTITIES, values);

  return diff(prevBasic, nextBasic);
}

export function normalizeDate(val) {
  if (!val) return '';
  return dayjs(val).isValid() ? dayjs(val).format('YYYY-MM-DD') : String(val);
}

export function normalizeEntityItem(item) {
  if (!item || typeof item !== 'object') return item;
  const clone = { ...item };

  ['expDate', 'assignDate', 'returnDate', 'dateEnd'].forEach(dateKey => {
    if (dateKey in clone) {
      clone[dateKey] = normalizeDate(clone[dateKey]);
    }
  });

  [
    'uid',
    'bankName',
    'relationship',
    'mobilePhone',
    'homePhone',
    'address',
    'degree',
    'institution',
    'model',
  ].forEach(strKey => {
    if (strKey in clone) {
      clone[strKey] = clone[strKey] == null ? '' : String(clone[strKey]).trim();
    }
  });

  if (
    clone.stateProperty &&
    typeof clone.stateProperty === 'object' &&
    clone.stateProperty.id !== undefined
  ) {
    clone.stateProperty = clone.stateProperty.id;
  }

  if (Array.isArray(clone.files)) {
    clone.files = clone.files
      .filter(f => f && (f.temp || f.deleted))
      .map(f => ({
        id: f.id,
        temp: Boolean(f.temp),
        deleted: Boolean(f.deleted),
      }));
  } else {
    clone.files = [];
  }

  return clone;
}

/**
 * Returns difference between employee entity(contacts, documents, educations, properties) values
 * @param {Employee} prevValues
 * @param {Employee} values
 * @returns {{ added: Object, deleted: Object, updated: Object }}
 */
export function getEmployeeEntitiesDiff(prevValues, values) {
  const prevEntities = normalizeEntities(prevValues, EMPLOYEE_LIST_ENTITIES);
  const nextEntities = normalizeEntities(values, EMPLOYEE_LIST_ENTITIES);

  const added = diffArrayEntities(
    nextEntities,
    prevEntities,
    item => item.id === undefined,
  );
  const deleted = diffArrayEntities(prevEntities, nextEntities, (item, next) =>
    next.every(i => i.id !== item.id),
  );
  const updated = diffArrayEntities(
    nextEntities,
    prevEntities,
    (item, next) => {
      const match = next.find(({ id }) => id === item.id);
      if (match) {
        return !isEmpty(
          diff(normalizeEntityItem(match), normalizeEntityItem(item)),
        );
      }
      return false;
    },
  );

  return { added, deleted, updated };
}

export function mapDataId({ data }) {
  return data ? data.id : '';
}

export function mapEntity(item) {
  return {
    ...item,
    files: item.files.map(file => ({ ...file, deleted: false })),
  };
}

export function normalizeEntities(values, entityKeys) {
  const {
    documents = [],
    contracts = [],
    curriculumVitae = [],
    ...entities
  } = pick(entityKeys, values);
  return {
    ...entities,
    documents: [...documents, ...contracts, ...curriculumVitae],
  };
}

/**
 *
 * @param {Object<string, Array>} left
 * @param {Object<string, Array>} right
 * @param {Function} comparator Calls with current iterating item and right array
 */
export function diffArrayEntities(
  left,
  right,
  comparator = (item, rightArray) => !rightArray.includes(item),
) {
  const diffed = mapObject(
    (arr, key) => diffArrays(arr, right[key], comparator),
    left,
  );

  return filterObject(val => !isEmpty(val), diffed);
}

/**
 *
 * @param {Employee} employee
 */
export function transformEmployee(employee) {
  const entities = EMPLOYEE_LIST_ENTITIES.reduce(
    (acc, entity) => ({
      ...acc,
      [entity]: employee[entity] || [],
    }),
    {},
  );

  const rawDocuments = entities.documents;
  const contractTypes = [DOCUMENTS.CONTRACT, DOCUMENTS.NDA];

  const contracts = rawDocuments.filter(item =>
    contractTypes.includes(item.docType),
  );
  const documents = rawDocuments.filter(
    item =>
      !contractTypes.includes(item.docType) && item.docType !== DOCUMENTS.CV,
  );
  const curriculumVitae = rawDocuments.filter(
    item => item.docType === DOCUMENTS.CV,
  );

  return {
    ...employee,
    curriculumVitae,
    fullName: getFullName(employee),
    documents: documents.map(mapEntity),
    contracts: contracts.map(mapEntity),
    educations: entities.educations.map(mapEntity),
    properties: entities.properties.map(mapEntity),
    contacts: entities.contacts,
  };
}

export function getEntityFiles(entity, predicate) {
  return entity && entity.files
    ? entity.files.reduce(
        (acc, file) => (predicate(file) ? acc.concat([file]) : acc),
        [],
      )
    : null;
}

export function getIqamaInfo(employee) {
  if (!employee) return null;
  const rawDocs = (employee.documents || []).concat(
    employee.rawDocuments || [],
  );
  const civilIdDoc = rawDocs.find(
    d =>
      d &&
      (d.docType === DOCUMENTS.CIVIL_ID ||
        d.docType === 'civil_id' ||
        d.docType === 'iqama'),
  );
  const expDate =
    employee.iqamaExpiry ||
    employee.iqamaExpDate ||
    employee.civilIdExpiry ||
    (civilIdDoc && civilIdDoc.expDate);
  const idNumber =
    employee.civilId ||
    employee.iqamaNumber ||
    employee.nationalId ||
    (civilIdDoc && civilIdDoc.number);

  if (!expDate && !idNumber) return null;

  let isExpired = false;
  let isUpcoming = false;
  let daysRemaining = null;

  if (expDate) {
    const expiry = dayjs(new Date(expDate));
    const now = dayjs();
    daysRemaining = expiry.diff(now, 'day');
    isExpired = daysRemaining < 0;
    isUpcoming = !isExpired && daysRemaining <= 60;
  }

  return {
    idNumber,
    expDate,
    isExpired,
    isUpcoming,
    daysRemaining,
  };
}

/**
 * Bank loan details come from the employee's 'bank_loan' documents
 * (bankName, expDate, uid). The documents array is only returned to authorized
 * viewers, so an absent array means "not visible", not "no loan".
 */
export function getBankLoanInfo(employee) {
  if (!employee || !Array.isArray(employee.documents)) return null;

  const loanDocs = employee.documents
    .filter(d => d && d.docType === DOCUMENTS.BANK_LOAN)
    .sort((a, b) => new Date(b.expDate || 0) - new Date(a.expDate || 0));

  if (loanDocs.length === 0) return null;

  const today = dayjs().startOf('day');
  const isCurrent = doc =>
    Boolean(doc.expDate) && !dayjs(doc.expDate).isBefore(today, 'day');
  const latest = loanDocs[0];

  return {
    // Display only: a loan document whose expiry date has not passed.
    hasActiveLoan: loanDocs.some(isCurrent),
    bankName: latest.bankName,
    expiryDate: latest.expDate,
    reference: latest.uid,
    documents: loanDocs,
  };
}
